import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useToast } from '@ais-ui/ui-core';
import {
  LOGOUT_REASON_SESSION_INVALID,
  getSessionLastActivity,
  hasSessionAnsweredSince,
  isExpectedSessionSignal,
  type LogoutContext,
  type LogoutSessionValidity,
  type Session,
  type Sessions,
  type SessionType,
} from '@ais-ui/auth';
import { checkSessionValidWithTimeout } from './ol-server/checkSessionValid';

/**
 * How long we tolerate `olSession` being unconfirmed (transport down, or a
 * session-invalid signal on an otherwise-open socket) before logging the
 * user out. One named constant, easy to retune from real rollout data
 * without hunting through the code.
 */
export const OL_SESSION_GRACE_PERIOD_MS = 45_000;

/**
 * Delay between successive reconnect-and-revalidate attempts while a grace
 * period is pending. A single reconnect can land on another replica that's
 * also mid-rollout, so we keep trying — this paces those retries so a run
 * of bad luck doesn't hammer the server in a tight loop. Independent of, and
 * much shorter than, the overall grace period deadline above.
 */
const VALIDITY_RETRY_DELAY_MS = 2_000;

/**
 * How often a pending grace period looks for a successful reply on the
 * current `olSession`. Overlays are polled every few seconds, so a working
 * socket answers well inside this.
 */
const RECOVERY_POLL_MS = 1_000;

interface ReconnectSuccessDetails {
  username?: string;
  sessionId?: string;
  /** The signal that started the grace period (same string that would have
   * been passed to `logout` had it ended in `reconnect_giveup` instead). */
  reason: string;
  /** How long the grace period was pending before the session was confirmed
   * valid again, in milliseconds. */
  recoveryMs: number;
  /** What proved the session valid: a successful reply on the current socket
   * (the normal case), or the explicit `checkSessionValid` RPC. */
  confirmedBy: 'reply' | 'validity_check';
}

/**
 * Bucket a recovery duration into a small, fixed set of labels so the
 * reconnect-time distribution is a queryable Sentry *tag* (tags are indexed;
 * `extra` is not). The boundaries are chosen against
 * {@link OL_SESSION_GRACE_PERIOD_MS}: a `30-45s` (or `45s+`) tail is the
 * tell-tale that slow-but-successful handoffs are brushing up against the
 * grace-period ceiling and that raising it would convert give-ups into
 * silent recoveries.
 */
function reconnectRecoveryBucket(recoveryMs: number): string {
  if (recoveryMs < 5_000) return '0-5s';
  if (recoveryMs < 15_000) return '5-15s';
  if (recoveryMs < 30_000) return '15-30s';
  if (recoveryMs < 45_000) return '30-45s';
  return '45s+';
}

/**
 * Build the `Sentry.captureMessage` arguments for a confirmed-valid reconnect
 * — the counterpart to `buildLogoutSentryEvent` in `AuthProvider.tsx`.
 * Reported at `info` level because this isn't a failure, it's the grace
 * period doing its job; capturing it anyway is what makes the recovered-vs-
 * `reconnect_giveup` ratio (and the recovery-time distribution) measurable
 * instead of only inferable from the absence of a logout. A pure function for
 * the same reason `buildLogoutSentryEvent` is one: testable without mocking
 * `@sentry/react` or rendering the hook.
 */
export function buildReconnectSuccessSentryEvent(
  sessionType: SessionType,
  {
    username,
    sessionId,
    reason,
    recoveryMs,
    confirmedBy,
  }: ReconnectSuccessDetails,
): { message: string; options: Parameters<typeof Sentry.captureMessage>[1] } {
  return {
    message: `Session reconnected: ${sessionType}`,
    options: {
      level: 'info',
      tags: {
        reconnect_session_type: sessionType,
        reconnect_recovery_bucket: reconnectRecoveryBucket(recoveryMs),
      },
      extra: {
        username,
        sessionId,
        reason,
        recoveryMs,
        confirmedBy,
      },
    },
  };
}

function describeSocket(session: Session | undefined) {
  const lastFrameAt = getSessionLastActivity(session);
  return {
    socketState: session?.webSocket?.readyState,
    msSinceLastFrame:
      lastFrameAt === undefined
        ? null
        : Math.round(performance.now() - lastFrameAt),
  };
}

type GracefulLogoutState =
  | { status: 'idle' }
  | { status: 'pending'; reason: string; sessionType: SessionType };

type SessionValidityResult = Awaited<
  ReturnType<typeof checkSessionValidWithTimeout>
>;

/**
 * True only when the backend itself refused the session: the validity check
 * answered `false`, or failed with a session/auth signal (`SessionNotFound`,
 * `SessionTimeout`, ...). A timeout, a transport drop, or any other backend
 * fault - e.g. a replica mid-rollout failing the RPC - says nothing about the
 * session, so it must not be read as "session expired".
 */
function isExplicitSessionRejection(result: SessionValidityResult): boolean {
  if (result.timedOut) return false;
  if (result.error === undefined) return true;
  return isExpectedSessionSignal(result.error);
}

export interface GracefulLogoutOptions {
  /** Skip the grace period and log out right away. Used both for deliberate
   * signals that must never be delayed (`NewLogonDetected`, a genuine
   * server-confirmed `SessionTimeout`) and for any `dbSession`/`replaySession`
   * failure — the pending-state effect below only ever reconnects/validates
   * `olSession`, so a non-`olSession` failure has no grace period to wait
   * out here; it logs out right away, same as before this hook existed. */
  immediate?: boolean;
  /** Which underlying websocket session reported the problem. Defaults to
   * `'olSession'`, the only session type that's actually load-bearing for
   * the grace period (see module docs). Always forwarded to `logout` — even
   * on the immediate path — purely so Sentry telemetry knows which session
   * triggered a forced logout. */
  sessionType?: SessionType;
}

export type GracefulLogout = (
  reason: string,
  options?: GracefulLogoutOptions,
) => void;

export interface GracefulLogoutResult {
  gracefulLogout: GracefulLogout;
  /** True exactly when a grace period is pending confirmation that
   * `olSession` is healthy again (`state.status === 'pending'`). Consumers
   * that need "is olSession actually down right now" — e.g. the "OLServer
   * down" badge — must use this instead of `sessions.olSession`, which
   * `SessionProvider` deliberately keeps truthy (a retained last-known-good
   * snapshot) for the whole grace period so the app doesn't unmount on
   * every blip. See `SessionProvider`'s module docs for why. */
  isOlSessionDown: boolean;
}

interface UseGracefulLogoutParams {
  sessions: Sessions;
  /** Current username, used to confirm the session is valid again. */
  username?: string;
  reconnect: (sessionType: string) => void;
  logout: (reason: string, context?: LogoutContext) => void;
}

/**
 * Single source of truth for "how long do we tolerate `olSession` being
 * unconfirmed before logging the user out."
 *
 * Two independent triggers exist today — a dropped transport
 * (`connectionClosed`) and a session-invalid signal on an otherwise-open
 * socket (`sessionError`) — and previously each owned its own competing
 * give-up logic (a ~75s retry-count ceiling in `useReconnect`, and an
 * instant logout in `SessionProvider`). Both now call this one function
 * instead, so there is exactly one clock and one place a developer needs to
 * look to understand when/why a forced logout happens.
 *
 * Behavior:
 * - `immediate: true` calls `logout` right away, no grace period.
 * - Otherwise, the *first* bad signal since the last confirmed-valid state
 *   starts a single {@link OL_SESSION_GRACE_PERIOD_MS} deadline. A
 *   second/third signal arriving while a grace period is already pending is
 *   a no-op — the existing deadline governs.
 * - While pending, an effect keeps working the problem: if there's no
 *   `olSession` at all, it reconnects; once a session exists, it actively
 *   confirms the session is valid (a reopened transport alone doesn't prove
 *   the backend still recognizes this sessionId). Only an explicit rejection
 *   by the backend (`false`, or a session/auth signal) means the session is
 *   gone and ends the period immediately (see `isExplicitSessionRejection`);
 *   a timeout, a transport drop or any other failure of the check means "try
 *   again".
 * - If the deadline elapses with no confirmation, `logout(reason, context)`
 *   is called with the session type and grace period duration attached, so
 *   Sentry telemetry carries full context on why.
 */
export function useGracefulLogout({
  sessions,
  username,
  reconnect,
  logout,
}: UseGracefulLogoutParams): GracefulLogoutResult {
  const { toast } = useToast();
  const [state, setState] = useState<GracefulLogoutState>({ status: 'idle' });

  // Latest-ref pattern (see react-best-practices skill, Rule 7): the pending
  // timer and effect below always act on the freshest callbacks without
  // needing to re-subscribe or recreate `gracefulLogout` every render.
  const stateRef = useRef(state);
  const logoutRef = useRef(logout);
  const reconnectRef = useRef(reconnect);
  const olSessionRef = useRef(sessions.olSession);
  const deadlineTimeoutIdRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const retryTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  // When the current grace period started, so a recovery can report how long
  // it took (see buildReconnectSuccessSentryEvent).
  const pendingStartedAtRef = useRef<number>(0);
  // Monotonically increasing generation for in-flight validation work.
  // Immediate logout / deadline expiry / unmount bump it so a late validity
  // response cannot surface success after the current grace period is gone.
  const validationGenerationRef = useRef(0);
  // What the most recent validity check said, reset per grace period, so the
  // eventual logout can report why the session was (or wasn't) confirmed.
  const lastValidityRef = useRef<LogoutSessionValidity | undefined>(undefined);

  // Keep a handle to the dismiss function for the one active pending
  // "checking connection" toast so lifecycle events (immediate logout,
  // success, failure, unmount) can dismiss it deterministically.
  const pendingToastDismissRef = useRef<(() => void) | undefined>(undefined);

  const dismissPendingToast = useCallback(() => {
    const dismiss = pendingToastDismissRef.current;
    pendingToastDismissRef.current = undefined;
    dismiss?.();
  }, []);

  const { olSession, sessionId } = sessions;

  /** Ends the pending grace period because the session is confirmed valid. */
  const confirmSession = useCallback(
    (confirmedBy: 'reply' | 'validity_check') => {
      const current = stateRef.current;
      if (current.status !== 'pending') {
        return;
      }
      clearTimeout(deadlineTimeoutIdRef.current);
      clearTimeout(retryTimeoutIdRef.current);
      validationGenerationRef.current += 1;
      stateRef.current = { status: 'idle' };
      setState({ status: 'idle' });
      dismissPendingToast();
      const { message, options } = buildReconnectSuccessSentryEvent(
        current.sessionType,
        {
          username,
          sessionId,
          reason: current.reason,
          recoveryMs: Date.now() - pendingStartedAtRef.current,
          confirmedBy,
        },
      );
      Sentry.captureMessage(message, options);
      toast({
        className: 'reconnect-success-toast',
        title: 'Reconnected',
        description: 'Your session is still active.',
      });
    },
    [dismissPendingToast, sessionId, toast, username],
  );

  /**
   * Ends the current grace period by logging the user out, if one is pending.
   *
   * Extracted from the deadline timer so it can also be driven by elapsed
   * wall-clock time — see `gracefulLogout`'s dedupe branch and the
   * visibility/pageshow watcher. A background tab (or one parked in the
   * Back-Forward Cache) throttles or freezes `setTimeout`, so the 45s deadline
   * can drift far past its nominal window; re-deriving "has the deadline
   * passed?" from `pendingStartedAtRef` keeps the grace period bounded to real
   * time regardless of timer scheduling.
   *
   * `reasonOverride` lets the validity check end the period as "session
   * invalid" rather than the transport-drop reason that started it, because
   * keeping the session alive is the backend's job: once it says the session
   * is gone, a reconnect can't bring it back.
   */
  const expireGracePeriod = useCallback(
    (reasonOverride?: string) => {
      const current = stateRef.current;
      if (current.status !== 'pending') {
        return;
      }

      // A reply the recovery poll hasn't noticed yet (a throttled background
      // tab) still proves the session; only the backend refusing it overrides.
      if (
        reasonOverride === undefined &&
        hasSessionAnsweredSince(
          olSessionRef.current,
          pendingStartedAtRef.current,
        )
      ) {
        confirmSession('reply');
        return;
      }

      clearTimeout(deadlineTimeoutIdRef.current);
      clearTimeout(retryTimeoutIdRef.current);
      validationGenerationRef.current += 1;
      stateRef.current = { status: 'idle' };
      setState({ status: 'idle' });
      dismissPendingToast();
      logoutRef.current(reasonOverride ?? current.reason, {
        sessionType: current.sessionType,
        gracePeriodMs: OL_SESSION_GRACE_PERIOD_MS,
        sessionValidity: lastValidityRef.current ?? {
          outcome: 'not_checked',
          checks: 0,
        },
      });
    },
    [confirmSession, dismissPendingToast],
  );

  /** True when a grace period has been pending for at least the full window of
   * real time, whether or not its `setTimeout` has been allowed to fire. */
  const hasGracePeriodElapsed = useCallback(
    () =>
      stateRef.current.status === 'pending' &&
      Date.now() - pendingStartedAtRef.current >= OL_SESSION_GRACE_PERIOD_MS,
    [],
  );

  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);
  useEffect(() => {
    reconnectRef.current = reconnect;
  }, [reconnect]);
  useEffect(() => {
    olSessionRef.current = sessions.olSession;
  }, [sessions.olSession]);

  const gracefulLogout = useCallback<GracefulLogout>(
    (reason, options = {}) => {
      const sessionType = options.sessionType ?? 'olSession';

      if (options.immediate) {
        clearTimeout(deadlineTimeoutIdRef.current);
        clearTimeout(retryTimeoutIdRef.current);
        validationGenerationRef.current += 1;
        stateRef.current = { status: 'idle' };
        setState({ status: 'idle' });
        dismissPendingToast();
        logoutRef.current(reason, { sessionType });
        return;
      }

      // Dedupe: a grace period is already running — the effect below is
      // already working the problem on the existing deadline; don't restart
      // the clock for a second/third signal. But if real time has already
      // outrun the deadline (the tab was backgrounded or frozen in the
      // Back-Forward Cache, where `setTimeout` did not fire), expire now
      // instead of deduping forever.
      if (stateRef.current.status === 'pending') {
        if (hasGracePeriodElapsed()) {
          expireGracePeriod();
        }
        return;
      }

      const nextState: GracefulLogoutState = {
        status: 'pending',
        reason,
        sessionType,
      };
      validationGenerationRef.current += 1;
      lastValidityRef.current = undefined;
      pendingStartedAtRef.current = Date.now();
      stateRef.current = nextState;
      setState(nextState);
      Sentry.addBreadcrumb({
        category: 'grace-period',
        level: 'info',
        message: 'Grace period started',
        data: { reason, sessionType, ...describeSocket(olSessionRef.current) },
      });

      const { dismiss } = toast({
        className: 'checking-connection-toast',
        title: 'Checking connection…',
        description: 'Verifying your session is still active.',
        duration: Infinity,
      });
      pendingToastDismissRef.current = dismiss;

      deadlineTimeoutIdRef.current = setTimeout(() => {
        expireGracePeriod();
      }, OL_SESSION_GRACE_PERIOD_MS);
    },
    [dismissPendingToast, expireGracePeriod, hasGracePeriodElapsed, toast],
  );

  // While pending, keep working towards a confirmed-valid session: reconnect
  // if there's no session to check, validate if there is, and reconnect again
  // if validation says it's still not good. Runs once per `olSession`
  // change (a fresh session from a completed reconnect) and once more after
  // each `VALIDITY_RETRY_DELAY_MS` if the check came back invalid.
  //
  // In parallel it watches for the cheapest proof there is: a successful reply
  // on the current `olSession`. That ends the period without waiting for the
  // validity check, and stops a slow check from replacing a socket that is
  // demonstrably serving the map.
  useEffect(() => {
    if (state.status !== 'pending') {
      return;
    }
    const { sessionType } = state;
    const validationGeneration = validationGenerationRef.current;
    let cancelled = false;

    const isStale = () =>
      cancelled || validationGeneration !== validationGenerationRef.current;

    const pollId = setInterval(() => {
      if (
        !isStale() &&
        hasSessionAnsweredSince(olSession, pendingStartedAtRef.current)
      ) {
        confirmSession('reply');
      }
    }, RECOVERY_POLL_MS);

    const cleanup = () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(retryTimeoutIdRef.current);
      validationGenerationRef.current += 1;
    };

    const retryReconnect = (delayMs: number) => {
      retryTimeoutIdRef.current = setTimeout(() => {
        if (!cancelled) {
          reconnectRef.current(sessionType);
        }
      }, delayMs);
    };

    // Only worth validating if the transport is actually open — a
    // validity RPC over a CLOSED/CLOSING socket can't succeed and just
    // burns most of its own timeout for nothing (checkSessionValidWithTimeout
    // defaults to 8s), starving the reconnect attempts that could actually
    // fix it before the grace period deadline. Reconnect immediately instead.
    const isSocketOpen = olSession?.webSocket?.readyState === WebSocket.OPEN;

    if (!olSession || !sessionId || !username || !isSocketOpen) {
      retryReconnect(0);
      return cleanup;
    }

    // Recorded before the RPC resolves so a deadline that fires mid-check
    // reports `in_progress` rather than claiming nothing was ever sent.
    const checkNumber = (lastValidityRef.current?.checks ?? 0) + 1;
    lastValidityRef.current = { outcome: 'in_progress', checks: checkNumber };

    checkSessionValidWithTimeout(olSession, sessionId, username)
      .then((result) => {
        if (isStale()) {
          return;
        }
        if (!result.valid) {
          lastValidityRef.current = {
            outcome: result.timedOut
              ? 'timed_out'
              : isExplicitSessionRejection(result)
                ? 'rejected'
                : 'error',
            error: result.error,
            checks: checkNumber,
          };
          // Keeping the session alive is the backend's job, so only the
          // backend explicitly refusing the session ends the period early: a
          // reconnect cannot bring that back. Anything else gets another turn
          // inside the grace window (see `isExplicitSessionRejection`).
          if (!isExplicitSessionRejection(result)) {
            retryReconnect(VALIDITY_RETRY_DELAY_MS);
            return;
          }
          expireGracePeriod(LOGOUT_REASON_SESSION_INVALID);
          return;
        }
        confirmSession('validity_check');
      })
      .catch((error: unknown) => {
        // checkSessionValidWithTimeout resolves rather than rejects on
        // failure; this is a defensive fallback in case that ever changes.
        if (!isStale()) {
          lastValidityRef.current = {
            outcome: 'error',
            error: error instanceof Error ? error.message : String(error),
            checks: checkNumber,
          };
          retryReconnect(VALIDITY_RETRY_DELAY_MS);
        }
      });

    return cleanup;
  }, [
    state,
    olSession,
    sessionId,
    username,
    confirmSession,
    expireGracePeriod,
  ]);

  // Re-check the deadline whenever the tab becomes visible again. A background
  // tab throttles `setTimeout` and a Back-Forward Cache restore freezes it
  // outright, so the deadline timer can fire long after the grace window has
  // actually elapsed (or be delayed indefinitely); this bounds the period to
  // real time instead. `pageshow` covers a bfcache restore, `focus` the common
  // "user came back to the tab" case, and `visibilitychange` the standard one.
  useEffect(() => {
    const checkElapsed = () => {
      if (hasGracePeriodElapsed()) {
        expireGracePeriod();
      }
    };

    document.addEventListener('visibilitychange', checkElapsed);
    window.addEventListener('focus', checkElapsed);
    window.addEventListener('pageshow', checkElapsed);

    return () => {
      document.removeEventListener('visibilitychange', checkElapsed);
      window.removeEventListener('focus', checkElapsed);
      window.removeEventListener('pageshow', checkElapsed);
    };
  }, [expireGracePeriod, hasGracePeriodElapsed]);

  // Clear any pending timers if the component unmounts mid-grace-period.
  useEffect(() => {
    return () => {
      validationGenerationRef.current += 1;
      clearTimeout(deadlineTimeoutIdRef.current);
      clearTimeout(retryTimeoutIdRef.current);
      dismissPendingToast();
    };
  }, [dismissPendingToast]);

  return { gracefulLogout, isOlSessionDown: state.status === 'pending' };
}
